import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { Rank, appDialogConfirm } from "@components";
import { Avatar } from "@features/Avatar";
import { useAuth, userId } from "@state/auth";
import dayjs from "dayjs";
import { Edit, EllipsisVertical, Reply, Trash2, X } from "lucide-solid";
import { For, Show, createSignal } from "solid-js";
import { useService } from 'solid-services';
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { Content } from "./Content";
import { Post } from "./Post";
import { useComments } from './state';
import type { CommentTreeItem } from './types';

interface CommentProps {
  comment: CommentTreeItem;
  isReply?: boolean;
  isLast?: boolean;
}

export const CommentItem = (props: CommentProps) => {
  const { activeReplyId, openReply, closeReply, editMutation, deleteMutation } = useService(useComments)();
  const { isAdmin } = useService(useAuth)();

  const [isEditing, setIsEditing] = createSignal(false);
  const [editContent, setEditContent] = createSignal(props.comment.content);

  const isReplying = () => activeReplyId() === props.comment.id;
  const canModify = () => isAdmin() || props.comment.profile_id === userId()!;

  const handleEdit = async () => {
    if (!editContent().trim() || editContent() === props.comment.content) {
      setIsEditing(false);
      return;
    }
    await editMutation.mutateAsync({ id: props.comment.id, content: editContent() });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    appDialogConfirm(
      () => deleteMutation.mutateAsync(props.comment.id),
      'Are you sure you want to delete this Comment?',
    )()
  };

  return (
    <Grid gap="3" mb={1} ml={props.isReply ? '3' : '0'}>
      <Grid class={css({
        gap: "3",
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'start',
        borderLeft: props.isReply ? 'solid 1px' : 'none',
        pl: props.isReply ? '3' : '0',
        borderColor: 'fg.muted',
        borderBottomLeftRadius: props.isReply && props.isLast ? '8px' : '0',
      })}>
        <Box w='full' height='50px'>
          <Avatar id={props.comment.profile.id} />
        </Box>
        <Grid gap='1'>
          <Flex align="center" gap="0">
            <Text
              fontWeight='bold'
              textTransform='capitalize'
              fontSize='10px'
              color='fg.subtle'
              children={props.comment.profile.username}
            />
            <Rank rank={props.comment.profile.profile_private?.role} />
          </Flex>
          <Text fontSize='9px' color='fg.subtle'>
            {dayjs(props.comment.created_at).format('MMMM D, YYYY h:mm A')}
          </Text>

          <Show
            when={!isEditing()}
            fallback={
              <Box mt="2" width="full">
                <Textarea
                  value={editContent()}
                  onInput={(e) => setEditContent(e.currentTarget.value)}
                  autofocus
                />
                <Flex justify="flex-end" gap="2" mt="2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={editMutation.isPending}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleEdit} loading={editMutation.isPending} disabled={!editContent().trim()}>
                    Save
                  </Button>
                </Flex>
              </Box>
            }
          >
            <Content content={props.comment.content} />
          </Show>

          <Show when={isReplying()}>
            <Box mt="2">
              <Post onCancel={closeReply} parentId={props.comment.id} />
            </Box>
          </Show>
        </Grid>

        <Flex gap="1" align="flex-start">
          <Show
            when={isReplying()}
            fallback={
              <Button variant="outline" size="2xs" color='fg.subtle' onClick={() => openReply(props.comment.id)}>
                Reply
                <Reply size={14} />
              </Button>
            }
          >
            <Button variant="outline" size="2xs" color='error.fg' onClick={closeReply}>
              <X size={14} />
              Cancel
            </Button>
          </Show>

          <Show when={canModify() && !isEditing()}>
            <Menu.Root
              onSelect={(details) => {
                if (details.value === 'edit') setIsEditing(true);
                if (details.value === 'delete') handleDelete();
              }}
            >
              <Menu.Trigger
                asChild={(triggerProps) => (
                  <IconButton variant="outline" size="2xs" color="fg.subtle" {...triggerProps()}>
                    <EllipsisVertical size={14} />
                  </IconButton>
                )}
              />
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item minH={8} value="edit">
                    <Edit size={14} />
                    <Text textStyle='xs'>Edit</Text>
                  </Menu.Item>
                  <Menu.Item minH={8} value="delete" color="error.fg">
                    <Trash2 size={14} />
                    <Text textStyle='xs'>Delete</Text>
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </Show>
        </Flex>
      </Grid>

      <For each={props.comment.replies}>
        {(reply, index) => (
          <CommentItem
            comment={reply}
            isReply
            isLast={index() === props.comment.replies.length - 1}
          />
        )}
      </For>
    </Grid>
  );
};
